import { DatasetLabel } from '@/components/filters/displayNames';
import Properties, { Property, Term, Value } from '@/components/properties';
import { Button } from '@/components/ui/button';
import { NotFoundLoaderResponse } from '@/errors';
import {
  DeprecatedTaxonRedirectQuery,
  DeprecatedTaxonRedirectQueryVariables,
  DeprecatedTaxonTombstoneQuery,
  DeprecatedTaxonTombstoneQueryVariables,
} from '@/gql/graphql';
import { DynamicLink, LoaderArgs } from '@/reactRouterPlugins';
import { ArticleIntro } from '@/routes/resource/key/components/articleIntro';
import { ArticlePreTitle } from '@/routes/resource/key/components/articlePreTitle';
import { ArticleTextContainer } from '@/routes/resource/key/components/articleTextContainer';
import { ArticleTitle } from '@/routes/resource/key/components/articleTitle';
import { PageContainer } from '@/routes/resource/key/components/pageContainer';
import { throwCriticalErrors } from '@/routes/rootErrorPage';
import { FormattedMessage } from 'react-intl';
import { json, redirect, useLoaderData } from 'react-router-dom';

export async function speciesLoader({ params, graphql, locale, config }: LoaderArgs) {
  const key = params.key as string;
  // if not a number just throw a 404
  if (isNaN(Number(key))) {
    throw new NotFoundLoaderResponse();
  }

  // Phase 1: cheap query with only the fields needed to decide the redirect target. ~80% of
  // /species hits are 302 redirects, so this avoids fetching the full classification block (only
  // used by the tombstone page)
  const response = await graphql.query<
    DeprecatedTaxonRedirectQuery,
    DeprecatedTaxonRedirectQueryVariables
  >(SPECIES_REDIRECT_QUERY, {
    key,
    newDatasetKey: config.defaultChecklistKey,
  });

  const { errors, data } = await response.json();
  throwCriticalErrors({
    path404: ['speciesKey'],
    errors,
    requiredObjects: [],
  });

  // if backbone key we can sometimes redirect to new CoL page
  const newTaxonID = data?.speciesKey?.colMatchId;
  if (newTaxonID && data?.speciesKey?.datasetKey === import.meta.env.PUBLIC_CLASSIC_BACKBONE_KEY) {
    return redirect(`${locale.gbifOrgLocalePrefix}/taxon/${newTaxonID}`);
  }
  const taxonId = data.speciesKey?.taxonID;
  const datasetKey = data.speciesKey?.datasetKey;
  const taxon = data?.speciesKey?.taxon;

  if (!taxonId || !datasetKey) {
    throw new NotFoundLoaderResponse();
  }

  // for species keys that refer to CoL, we just need to remap
  if (taxonId && datasetKey && datasetKey === import.meta.env.PUBLIC_COL_CHECKLIST_KEY) {
    return redirect(`${locale.gbifOrgLocalePrefix}/taxon/${encodeURIComponent(taxonId)}`);
  }

  // for anything that isn't the backbone or CoL we can redirect to the dataset specific page
  if (
    taxon &&
    taxonId &&
    datasetKey &&
    datasetKey !== import.meta.env.PUBLIC_CLASSIC_BACKBONE_KEY
  ) {
    return redirect(
      `${locale.gbifOrgLocalePrefix}/dataset/${datasetKey}/taxon/${encodeURIComponent(taxonId)}`
    );
  }

  // It is not a mappable backbone key, not a CoL key and not another known species key.
  // so it must be a backbone key that has no mapping to CoL.
  // Phase 2: only now do we fetch the full record needed to render the tombstone page.
  const tombstoneResponse = await graphql.query<
    DeprecatedTaxonTombstoneQuery,
    DeprecatedTaxonTombstoneQueryVariables
  >(SPECIES_TOMBSTONE_QUERY, { key });
  const { errors: tombstoneErrors, data: tombstoneData } = await tombstoneResponse.json();

  return json(
    { errors: tombstoneErrors, data: tombstoneData },
    {
      status: 404,
    }
  );
}

export function SpeciesKey() {
  const { data } = useLoaderData() as { data: DeprecatedTaxonTombstoneQuery };
  const speciesKey = data.speciesKey;
  const isBackbone = data.speciesKey?.datasetKey === import.meta.env.PUBLIC_CLASSIC_BACKBONE_KEY;

  return (
    <article>
      <PageContainer topPadded bottomPadded className="g-bg-white">
        <ArticleTextContainer>
          <ArticlePreTitle clickable>
            <DynamicLink pageId="taxonSearch">Taxon</DynamicLink>
          </ArticlePreTitle>
          <ArticleTitle>{speciesKey?.scientificName ?? 'Unknown taxon'}</ArticleTitle>
          <ArticleIntro>
            <p className="g-text-red-500 g-text-base g-font-medium g-pb-2">
              <FormattedMessage
                id="taxon.deletedMessage"
                defaultMessage="This record has been deleted."
              />
            </p>
            <p className="g-text-base">
              <FormattedMessage
                id="taxon.deletedDescription"
                defaultMessage="This record has been deleted."
              />
            </p>
          </ArticleIntro>
        </ArticleTextContainer>
        <div className="g-py-8">
          <div className="g-bg-slate-100 g-p-4 md:g-p-8 g-w-full g-max-w-7xl g-m-auto g-overflow-auto">
            <Properties>
              <Property labelId="Scientific name" value={speciesKey?.scientificName} />
              <Property labelId="status" value={speciesKey?.taxonomicStatus} />
              <Property labelId="rank" value={speciesKey?.rank} />
              <Property labelId="deleted" value={speciesKey?.deleted} />
              {speciesKey?.datasetKey && (
                <Property labelId="dataset" value={speciesKey?.datasetKey}>
                  <DynamicLink
                    className="g-underline"
                    pageId="datasetKey"
                    variables={{ key: speciesKey?.datasetKey ?? '' }}
                  >
                    <DatasetLabel id={speciesKey?.datasetKey} /> ({speciesKey?.datasetKey})
                  </DynamicLink>
                </Property>
              )}
              <>
                <Term>Classification</Term>
                <Value>
                  <Properties>
                    {['kingdom', 'phylum', 'class', 'order', 'family', 'genus', 'species'].map(
                      (rank) => (
                        <Property
                          key={rank}
                          labelId={`${rank}:`}
                          value={
                            speciesKey?.[rank as keyof typeof speciesKey] as string | undefined
                          }
                        />
                      )
                    )}
                  </Properties>
                </Value>
              </>
            </Properties>
          </div>
          <div className="g-max-w-7xl g-m-auto g-mt-4">
            {isBackbone && (
              <Button asChild className="g-w-full">
                <DynamicLink
                  pageId="taxonSearch"
                  searchParams={{
                    q: speciesKey?.scientificName ?? '',
                  }}
                >
                  Go to taxon search
                </DynamicLink>
              </Button>
            )}

            {!isBackbone && speciesKey?.datasetKey && (
              <Button asChild className="g-w-full">
                <DynamicLink
                  pageId="datasetKey"
                  variables={{ key: speciesKey?.datasetKey }}
                  searchParams={{
                    q: speciesKey?.scientificName ?? '',
                  }}
                >
                  Go to dataset page
                </DynamicLink>
              </Button>
            )}
          </div>
        </div>
      </PageContainer>
    </article>
  );
}

// Phase 1 — minimal fields needed to decide the redirect target (the common, ~80% path).
const SPECIES_REDIRECT_QUERY = /* GraphQL */ `
  query DeprecatedTaxonRedirect($key: ID!, $newDatasetKey: ID!) {
    speciesKey(key: $key) {
      taxonID
      datasetKey
      colMatchId
      taxon {
        related(datasetKey: [$newDatasetKey]) {
          taxonID
        }
      }
    }
  }
`;

// Phase 2 — the full record the tombstone page renders. Fetched only when a /species key has no
// modern equivalent to redirect to (~20% of requests). Drops the unused taxon/parentTree block.
const SPECIES_TOMBSTONE_QUERY = /* GraphQL */ `
  query DeprecatedTaxonTombstone($key: ID!) {
    speciesKey(key: $key) {
      taxonID
      datasetKey
      kingdom
      phylum
      class
      order
      family
      genus
      species
      scientificName
      taxonomicStatus
      rank
      deleted
    }
  }
`;
